import React, { useMemo, type ComponentType } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import { SpotlightManager, SpotlightTarget } from '@atlaskit/onboarding';
import { Box, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { IssueBreakdownButton } from '@atlassian/jira-ai-work-breakdown/src/ui/issue-breakdown-button/index.tsx';
import { useIsAiEnabledForIssue } from '@atlassian/jira-issue-context-service/src/main.tsx';
import type { ChildIssue } from '@atlassian/jira-issue-shared-types/src/common/types/children-issues-type.tsx';
import type { ChildIssuesLimitStatus } from '@atlassian/jira-issue-view-common-constants/src/child-issues.tsx';
import { SectionHeadingIcons } from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import type { MainIssueAggQueryRelayFragment } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-agg-data/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import type { RefObject } from '@atlassian/jira-shared-types/src/general.tsx';
import { hasExceededIssuesLimitInitialLoad } from '../../../common/utils/has-exceeded-issues-limit/index.tsx';
import { useIsAiEnabled } from '../../utils.tsx';
import type { AddChildEventHandler } from '../add-child/types.tsx';

type ConnectProps = {
	Meatballs: ComponentType<{
		childIssuesLimitStatus: ChildIssuesLimitStatus;
		isSortingAvailable?: boolean;
		rootRelayFragment: MainIssueAggQueryRelayFragment | null;
		isHideDoneFilterEnabled: boolean | null | undefined;
		// TODO: Make required on hide-rank-option-when-unavailable cleanup
		isRankingEnabled?: boolean;
	}>;
	AddChild: ComponentType<{
		onClick?: AddChildEventHandler;
	}>;

	allIssues: ChildIssue[];
	supportsIssueCreation: boolean | undefined;
};

type ParentProps = {
	childIssuesLimitStatus: ChildIssuesLimitStatus;
	issueHierarchyLevel?: number | undefined;
	onAddChildClick: AddChildEventHandler | undefined;
	rootRelayFragment: MainIssueAggQueryRelayFragment | null;
	isHideDoneFilterEnabled: boolean | null | undefined;
	// TODO: Make required on hide-rank-option-when-unavailable cleanup
	isRankingEnabled?: boolean;
	columnPickerRef?: RefObject<HTMLDivElement>;
};

export type Props = ConnectProps & ParentProps;

function HeaderSectionHeadingIcons({
	Meatballs,
	AddChild,
	supportsIssueCreation,
	allIssues,
	onAddChildClick,
	childIssuesLimitStatus,
	issueHierarchyLevel,
	rootRelayFragment,
	isHideDoneFilterEnabled,
	isRankingEnabled,
	columnPickerRef,
}: Props) {
	const isMeatballMenuEnabled =
		hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus) || allIssues.length > 0;

	const isAiEnabled = useIsAiEnabled();

	const isAiOptInEnabled = useIsAiEnabledForIssue();

	const isAnonymousUser = useIsAnonymous();

	const renderIssueBreakdownButton = useMemo(() => {
		return (
			<SpotlightManager>
				<SpotlightTarget name="suggestIssuesSpotlight">
					<IssueBreakdownButton issueHierarchyLevel={issueHierarchyLevel} isCompact />
				</SpotlightTarget>

				<EngagementSpotlight engagementId="suggestIssuesSpotlight" />
			</SpotlightManager>
		);
	}, [issueHierarchyLevel]);

	return (
		<SectionHeadingIcons>
			{!isAnonymousUser &&
			supportsIssueCreation &&
			isAiEnabled &&
			isAiOptInEnabled &&
			!expVal('proactive_aiwb_2', 'enabled', false) ? (
				<Box xcss={issueBreakdownButtonStyles}>{renderIssueBreakdownButton}</Box>
			) : null}
			{isMeatballMenuEnabled ? (
				<Meatballs
					childIssuesLimitStatus={childIssuesLimitStatus}
					isSortingAvailable
					rootRelayFragment={rootRelayFragment}
					isHideDoneFilterEnabled={isHideDoneFilterEnabled}
					isRankingEnabled={fg('hide-rank-option-when-unavailable') ? isRankingEnabled : undefined}
				/>
			) : null}
			{fg('jira-nike-chin-config-button-in-header') ? <Box ref={columnPickerRef} /> : null}
			{supportsIssueCreation ? <AddChild onClick={onAddChildClick} /> : null}
		</SectionHeadingIcons>
	);
}

export default HeaderSectionHeadingIcons;

const issueBreakdownButtonStyles = xcss({
	marginRight: 'space.075',
});
